import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import { ApolloProvider, useLazyQuery } from 'react-apollo';
import { useInView } from 'react-intersection-observer';
import isEqual from 'lodash.isequal';
import { client } from './client';
import cityCoordinates from '../constants/city_coordinates.json';
import cityIds from '../constants/city_ids.json';
import { MAP_LISTINGS_QUERY, nearbyRentalsQuery } from '../constants/queries';
import ListingMarker from './Map/ListingMarker';
import RentalMarker from './Map/RentalMarker';
import WhiteMarker from './WhiteMarker';

function NearbyMap({ area = '', community }) {
  const [underlineIndex, setUnderlineIndex] = useState(0);
  const [mapCenter, setMapCenter] = useState(cityCoordinates.destin);
  const [bounds, setBounds] = useState(null);
  const [isType, setIsType] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapRendered, setMapRendered] = useState(false);

  const isPropertyQuery = isType === null;
  const parsedArea = area.toLowerCase().replace(/\s/g, '-');
  const { sw, ne } = bounds || {};
  const stringPolygon = bounds
    ? `[${sw.lng.toFixed(6)},${sw.lat.toFixed(6)},${ne.lng.toFixed(
        6
      )},${ne.lat.toFixed(6)}]`
    : `[]`;

  const listingVariables = {
    is_type: isType,
    city_name_cont: parsedArea,
    in_polygon: bounds ? [sw.lng, sw.lat, ne.lng, ne.lat] : [],
  };

  const propertyVariables = {
    comm_id: community ? community.id : null,
    city_id: parseInt(cityIds[parsedArea]),
    polygon: stringPolygon,
  };

  useEffect(() => {
    if (area === 'Miramar Beach') {
      setMapCenter(cityCoordinates.sandestin);
    } else if (area === 'Fort Walton Beach') {
      setMapCenter(cityCoordinates['ft-walton-beach']);
    } else if (area === '30A') {
      setMapCenter(cityCoordinates['30a']);
    } else if (area === 'Destin') {
      setMapCenter([30.39431, -86.413706]);
      // setMapCenter(cityCoordinates.destin);
    } else if (community) {
      setMapCenter({
        lat: parseFloat(community.latitude),
        lng: parseFloat(community.longitude),
      });
    }
  }, [area, community]);

  const handleClick = e => {
    setUnderlineIndex(e.target.getAttribute('index'));
    setIsType(e.target.getAttribute('name'));
  };

  const variables = { per: 20 };

  const [
    fetchNearbyThings,
    { data, loading, error, variables: returnedVars },
  ] = useLazyQuery(isPropertyQuery ? nearbyRentalsQuery : MAP_LISTINGS_QUERY);

  if (error) console.error(error);

  const [nearbyRentalsRef, inView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (inView) {
      const queryVars = isPropertyQuery
        ? { ...variables, ...propertyVariables }
        : { ...variables, ...listingVariables };
      const sameVars = isEqual(queryVars, returnedVars);

      if (!sameVars) fetchNearbyThings({ variables: queryVars });
      if (!mapRendered) setMapRendered(true);
    }
  }, [
    fetchNearbyThings,
    inView,
    isPropertyQuery,
    listingVariables,
    mapRendered,
    propertyVariables,
    returnedVars,
    variables,
  ]);

  const query = isPropertyQuery ? 'properties' : 'listings';
  const showMarkers = data && data[query] && !loading;

  return (
    <section
      ref={nearbyRentalsRef}
      className="area-map full-page-width no-padding"
    >
      <ul className="area-map-nav">
        {/* eslint-disable */}
        <li onClick={handleClick} index={0} className="stay active">
          Stay
        </li>
        <li onClick={handleClick} name="activities" index={1} className="play">
          Play
        </li>
        <li onClick={handleClick} name="shopping" index={2} className="shop">
          Shop
        </li>
        <li onClick={handleClick} name="restaurants" index={3} className="dine">
          Dine
        </li>
        <li onClick={handleClick} name="services" index={4} className="hire">
          Hire
        </li>
        <div className="area-map-nav-underline" style={{ transform: `translateX(calc(55px * ${underlineIndex}))` }} />
      </ul>

      <section className="map full">
        <div style={{ width: '100%', height: '100%' }}>
          {loading && (
            <div className="loading-markers-overlay">
              <div className="loading-spinner" />
            </div>
          )}
          {mapRendered && (
            <GoogleMapReact
            bootstrapURLKeys={{
              // TODO: REPLACE WITH ENV VAR
              key: 'AIzaSyATvnQePj9OTTCZJ6VkugYM8dRGFPJ5HmQ',
            }}
            center={mapCenter}
            defaultZoom={13}
            onChange={({ bounds: newBounds }) => setBounds(newBounds)}
          >
            {community && (
              <WhiteMarker
                type="communities"
                lat={community.latitude}
                lng={community.longitude}
              />
            )}
            {showMarkers && data[query].collection.map(listing => (
              listing.is_type ? (
                <ListingMarker
                  key={listing.id}
                  lat={listing.latitude}
                  lng={listing.longitude}
                  listing={listing}
                  white_marker
                  type={listing.is_type}
                  changeActiveMarker={setActiveMarker}
                  activeMarker={activeMarker}
                />
              ) : (
                <RentalMarker
                  key={listing.id}
                  lat={listing.latitude}
                  lng={listing.longitude}
                  property={listing}
                  activeMarker={activeMarker}
                  changeActiveMarker={setActiveMarker}
                />
              )
            ))}
          </GoogleMapReact>
          )}
        </div>
      </section>
    </section>
  );
}

NearbyMap.propTypes = {
  area: PropTypes.string,
  community: PropTypes.object,
};

export default function NearbyMapWrapper({ ...props }) {
  return (
    <ApolloProvider client={client}>
      <NearbyMap {...props} />
    </ApolloProvider>
  )
}

